/**
 * Раздел «Социальная сфера» страницы «Аналитика» (redesign по макету):
 * заголовок секции с линией и иконкой + ряд стат-карточек + карточка
 * «Самообеспеченность продуктами питания» (две колонки строк с барами) +
 * пара таблиц «Образование» / «Здравоохранение» (ИТОГО + строки по типам).
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsSocial.json; остальные КАТО (по выбранному
 * в сайдбаре) — один запрос /api/public/analytics/social (DP-129,
 * US-04.4): plashka → стат-карточки (безработные, самообеспеченность,
 * школы, учащиеся, мед. объекты), self_sufficiency →
 * «Самообеспеченность» (продукт + %), education → «Образование»,
 * healthcare → «Здравоохранение» (названия строк — готовые строки из БД).
 */
import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import analyticsSocial from '../../../hardData/analyticsSocial.json';
import { getAnalyticsSocial } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type { SocialAnalyticsResponse } from '../../../types/analytics';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** Порядок продуктов «Самообеспеченности» по постановке. */
const FOOD_ORDER = [
  'BEEF',
  'LAMB',
  'CHICKEN',
  'ONION',
  'CUCUMBER',
  'CARROT',
  'POTATO',
  'MILK',
  'EGG',
  'TOMATO',
];
const foodOrderIndex = (subtype: string): number => {
  const index = FOOD_ORDER.indexOf(subtype);
  return index === -1 ? FOOD_ORDER.length : index;
};

interface FoodRowVM {
  key: string;
  name: string;
  percent: string;
  barPercent: number;
}

interface SocialCardVM {
  key: string;
  value: string;
  unit: string | null;
  trend: { text: string; down?: boolean } | null;
  footnotes: Array<{ text: string; bold?: boolean }>;
}

interface SocialTableVM {
  /** Заголовок колонки названий (US-04.4: «Сеть медицинских объектов»). */
  nameColumn?: string | null;
  /** Заголовки числовых колонок (1 или 2). */
  columns: string[];
  total: { count: string; amount: string | null } | null;
  rows: Array<{
    key: string;
    name: string;
    count: string;
    amount: string | null;
  }>;
}

interface SocialSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты —
   * «Образование» и «Здравоохранение» (FR-2.2). */
  summary?: boolean;
}

export const SocialSection = ({
  summary = false,
}: SocialSectionProps = {}) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.resolvedLanguage === 'kz';
  const { cards, selfSufficiency } = analyticsSocial;

  const { data, loading, isHardKato } =
    useAnalyticsSection<SocialAnalyticsResponse>(
      'social',
      getAnalyticsSocial,
    );

  // --- Вью-модели: мок для демо-КАТО, /analytics/social для остальных ---

  const socialCardVMs: SocialCardVM[] = isHardKato
    ? cards.map((card) => ({
        key: card.key,
        value: card.value,
        // «Самообеспеченность»: unit null — только % без подписи «ИФО».
        unit: card.unit ? (isKz ? card.unit.kz : card.unit.ru) : null,
        trend: card.trend,
        // Годовые сноски убраны из мока; массив в JSON пуст.
        footnotes: [],
      }))
    : (() => {
        const plashka = data?.plashka;

        const numCard = (
          key: string,
          value: number | null,
          unit: string,
        ): SocialCardVM => ({
          key,
          value: value !== null ? fmt(value) : '—',
          unit,
          trend: null,
          footnotes: [],
        });

        return [
          numCard(
            'unemployed',
            plashka?.unemployed ?? null,
            t('units.people'),
          ),
          {
            // Среднее по продуктам (считается на бэке); только %.
            key: 'selfSufficiency',
            value:
              plashka?.self_sufficiency_pct != null
                ? `${fmt1(plashka.self_sufficiency_pct)}%`
                : '—',
            unit: null,
            trend: null,
            footnotes: [],
          },
          numCard(
            'schools',
            plashka?.total_schools ?? null,
            t('units.pieces'),
          ),
          numCard(
            'students',
            plashka?.total_students ?? null,
            t('units.people'),
          ),
          numCard(
            'hospitals',
            plashka?.total_hospitals ?? null,
            t('units.pieces'),
          ),
        ];
      })();

  // «Самообеспеченность»: self_sufficiency ответа; бары — доля
  // от максимального процента (значения могут превышать 100%).
  // Продукты и их порядок — по постановке (FOOD_ORDER), подписи из i18n.
  const productName = (subtype: string): string =>
    t(`analytics.social.selfSufficiency.products.${subtype}`, {
      defaultValue: subtype,
    });
  const foodRows: FoodRowVM[] = isHardKato
    ? selfSufficiency.rows.map((row) => ({
        key: row.key,
        name: productName(row.key),
        percent: row.percent,
        barPercent: row.barPercent,
      }))
    : (() => {
        const list = (data?.self_sufficiency ?? []).filter(
          (f) => f.pct !== null,
        );
        const max = Math.max(1, ...list.map((f) => f.pct ?? 0));
        return [...list]
          .sort(
            (a, b) => foodOrderIndex(a.product) - foodOrderIndex(b.product),
          )
          .map((f) => ({
            key: f.product,
            name: productName(f.product),
            percent: `${fmt1(f.pct ?? 0)}%`,
            barPercent: Math.max(2, ((f.pct ?? 0) / max) * 100),
          }));
      })();

  /** Таблица из строк «название + 1–2 числа»: ИТОГО — суммы по колонкам. */
  const tableFromRows = (
    rows: Array<{ name: string; count: number | null; amount: number | null }>,
    columns: string[],
    withAmount: boolean,
    nameColumn?: string,
  ): SocialTableVM => {
    const sum = (values: Array<number | null>): string =>
      values.some((v) => v !== null)
        ? fmt(values.reduce<number>((acc, v) => acc + (v ?? 0), 0))
        : '—';
    return {
      nameColumn: nameColumn ?? null,
      columns,
      total:
        rows.length > 0
          ? {
              count: sum(rows.map((r) => r.count)),
              amount: withAmount ? sum(rows.map((r) => r.amount)) : null,
            }
          : null,
      rows: rows.map((row, index) => ({
        key: String(index),
        name: row.name,
        count: row.count !== null ? fmt(row.count) : '—',
        amount: withAmount
          ? row.amount !== null
            ? fmt(row.amount)
            : '—'
          : null,
      })),
    };
  };

  // Заголовки колонок — из i18n в обоих режимах: образование —
  // «Количество школ, ед.» + «Учащиеся»; здравоохранение — одна
  // колонка «шт.» и подпись «Сеть медицинских объектов» слева (US-04.4).
  const educationColumns = [
    t('analytics.social.education.columnCount'),
    t('analytics.social.education.columnAmount'),
  ];
  const healthcareColumns = [t('analytics.social.healthcare.columnCount')];
  const healthcareNameColumn = t('analytics.social.healthcare.columnName');

  const mockEducation = (): SocialTableVM => ({
    columns: educationColumns,
    total: {
      count: analyticsSocial.education.total.count,
      amount: analyticsSocial.education.total.amount,
    },
    rows: analyticsSocial.education.rows.map((row) => ({
      key: row.key,
      name: isKz ? row.name.kz : row.name.ru,
      count: row.count,
      amount: row.amount,
    })),
  });

  const mockHealthcare = (): SocialTableVM => ({
    nameColumn: healthcareNameColumn,
    columns: healthcareColumns,
    total: { count: analyticsSocial.healthcare.total.count, amount: null },
    rows: analyticsSocial.healthcare.rows.map((row) => ({
      key: row.key,
      name: isKz ? row.name.kz : row.name.ru,
      count: row.count,
      amount: null,
    })),
  });

  const tables: Record<'education' | 'healthcare', SocialTableVM> = {
    education: isHardKato
      ? mockEducation()
      : tableFromRows(
          (data?.education ?? []).map((row) => ({
            name: row.type,
            count: row.schools,
            amount: row.students,
          })),
          educationColumns,
          true,
        ),
    healthcare: isHardKato
      ? mockHealthcare()
      : tableFromRows(
          (data?.healthcare ?? []).map((row) => ({
            name: row.type,
            count: row.count,
            amount: null,
          })),
          healthcareColumns,
          false,
          healthcareNameColumn,
        ),
  };

  return (
    <section className="space-y-4" data-testid="analytics-social">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.social.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <Copy
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {!summary && (
        <>
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5">
        {socialCardVMs.map((card) => (
          <StatCard
            key={card.key}
            testId={`analytics-card-${card.key}`}
            title={t(`analytics.social.cards.${card.key}`)}
            value={card.value}
            unit={card.unit}
            trend={card.trend}
            footnotes={card.footnotes}
          />
        ))}
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>{t('analytics.social.selfSufficiency.title')}</CardTitle>
          <p className="text-xs text-text-muted">
            {t('analytics.social.selfSufficiency.subtitle')}
          </p>
        </CardHeader>
        <CardContent>
          {foodRows.length === 0 ? (
            <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
          ) : (
            <div
              className="grid gap-x-10 xl:grid-cols-2"
              data-testid="analytics-self-sufficiency"
            >
              {/* Две колонки, как на макете: первая половина списка слева. */}
              {[
                foodRows.slice(0, Math.ceil(foodRows.length / 2)),
                foodRows.slice(Math.ceil(foodRows.length / 2)),
              ].map((rows, columnIndex) => (
                <ul
                  key={columnIndex}
                  className={
                    columnIndex === 1
                      ? 'lg:border-l lg:border-surface-border lg:pl-10'
                      : undefined
                  }
                >
                  {rows.map((row) => (
                    <li
                      key={row.key}
                      data-testid={`analytics-food-row-${row.key}`}
                      className="flex items-center gap-4 border-b border-surface-border py-3 text-sm last:border-b-0"
                    >
                      <span className="w-28 shrink-0 truncate font-medium">
                        {row.name}
                      </span>
                      <span className="h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted">
                        <span
                          className="block h-full rounded-full bg-chart-blue"
                          style={{ width: `${row.barPercent}%` }}
                        />
                      </span>
                      <span className="w-16 shrink-0 text-right font-bold tabular-nums text-chart-indigo">
                        {row.percent}
                      </span>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
        </>
      )}

      <div className="grid gap-3.5 xl:grid-cols-2">
        {(['education', 'healthcare'] as const).map((tableKey) => {
          const table = tables[tableKey];
          return (
            <Card key={tableKey} className="shadow-card">
              <CardHeader>
                <CardTitle>{t(`analytics.social.${tableKey}.title`)}</CardTitle>
              </CardHeader>
              <CardContent>
                {table.rows.length === 0 ? (
                  <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
                ) : (
                  <div data-testid={`analytics-${tableKey}`}>
                    <div className="flex items-end gap-3 px-3 pb-2 text-sm text-text-faint">
                      <span className="min-w-0 flex-1 leading-snug">
                        {table.nameColumn}
                      </span>
                      {table.columns.map((column) => (
                        <span
                          key={column}
                          className="w-24 shrink-0 text-right leading-snug"
                        >
                          {column}
                        </span>
                      ))}
                    </div>

                    {table.total && (
                      <div
                        className="flex items-center gap-3 rounded-lg bg-surface-muted/60 p-3 text-sm font-extrabold"
                        data-testid={`analytics-${tableKey}-total`}
                      >
                        <span className="min-w-0 flex-1 uppercase">
                          {t('analytics.social.totalLabel')}
                        </span>
                        <span className="w-24 shrink-0 text-right tabular-nums">
                          {table.total.count}
                        </span>
                        {table.total.amount !== null && (
                          <span className="w-24 shrink-0 text-right tabular-nums">
                            {table.total.amount}
                          </span>
                        )}
                      </div>
                    )}

                    <ul>
                      {table.rows.map((row) => (
                        <li
                          key={row.key}
                          data-testid={`analytics-${tableKey}-row-${row.key}`}
                          className="flex items-center gap-3 border-b border-surface-border px-3 py-3.5 text-sm last:border-b-0 last:pb-0"
                        >
                          <span className="min-w-0 flex-1 truncate font-medium">
                            {row.name}
                          </span>
                          <span className="w-24 shrink-0 text-right font-semibold tabular-nums">
                            {row.count}
                          </span>
                          {row.amount !== null && (
                            <span className="w-24 shrink-0 text-right font-semibold tabular-nums">
                              {row.amount}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
        </>
      )}
    </section>
  );
};
